import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Chip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  LocalOffer as LocalOfferIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { httpClient } from '../services/httpClient';
import { ENDPOINTS } from '../config/api.config';

const AdminEditVendor = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    business_name: '',
    address: '',
    city_id: '',
    area_id: '',
    description: '',
    is_verified: false,
    working_hours: '',
    lat: '',
    lng: '',
    selectedTags: [],
  });

  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);
  const daysList = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const [workingDays, setWorkingDays] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [timeRange, setTimeRange] = useState({ start: '09:00', end: '18:00' });

  useEffect(() => {
    loadData();
  }, [id]);

  const parseHours = (hoursString) => {
    try {
      const match = hoursString.match(/(.+?)\s*\((\d{2}:\d{2})-(\d{2}:\d{2})\)/);
      if (match) {
        const daysStr = match[1];
        const start = match[2];
        const end = match[3];
        const days = daysStr.split(',').map(d => d.trim());
        return { days, start, end };
      }
    } catch (err) {
      console.error('Error parsing hours:', err);
    }
    return { days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'], start: '09:00', end: '18:00' };
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [vendorResponse, citiesResponse, tagsResponse] = await Promise.all([
        httpClient.get(`${ENDPOINTS.ADMIN_API}/vendors/${id}`),
        httpClient.get(ENDPOINTS.CITIES),
        httpClient.get(ENDPOINTS.TAGS),
      ]);

      let vendorData = vendorResponse;
      if (vendorResponse?.data && vendorResponse.data.id) {
        vendorData = vendorResponse.data;
      }

      if (!vendorData || !vendorData.id) {
        console.warn('Invalid vendor response:', vendorResponse);
        setError('Unable to load vendor data - invalid response');
        setLoading(false);
        return;
      }

      console.log('Vendor data loaded:', vendorData);

      setFormData({
        name: vendorData.name || '',
        email: vendorData.email || '',
        password: '',
        phone: vendorData.phone || '',
        business_name: vendorData.business_name || '',
        address: vendorData.address || '',
        city_id: vendorData.city_id || '',
        area_id: vendorData.area_id || '',
        description: vendorData.description || '',
        is_verified: vendorData.is_verified || false,
        working_hours: vendorData.working_hours || '',
        lat: vendorData.lat || '',
        lng: vendorData.lng || '',
        selectedTags: vendorData.tags || [],
      });

      // Parse working hours
      if (vendorData.working_hours) {
        const parsed = parseHours(vendorData.working_hours);
        setWorkingDays(parsed.days);
        setTimeRange(parsed);
      }

      let citiesData = Array.isArray(citiesResponse)
        ? citiesResponse
        : Array.isArray(citiesResponse?.data)
        ? citiesResponse.data
        : [];
      setCities(citiesData);

      let tagsData = Array.isArray(tagsResponse)
        ? tagsResponse
        : Array.isArray(tagsResponse?.data)
        ? tagsResponse.data
        : [];
      setAllTags(tagsData);

      if (vendorData.city_id) {
        await fetchAreas(vendorData.city_id);
      }
    } catch (err) {
      console.error('Error loading vendor:', err);
      setError(err.message || 'Failed to load vendor data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAreas = async (cityId) => {
    try {
      const response = await httpClient.get(`${ENDPOINTS.AREAS}?city_id=${cityId}`);
      let areasData = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];
      setAreas(areasData);
    } catch (err) {
      console.error('Error fetching areas:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    setFormData(prev => ({ ...prev, [name]: val }));

    if (name === 'city_id' && value) {
      fetchAreas(value);
      setFormData(prev => ({ ...prev, area_id: '' }));
    }
  };

  const toggleDay = (day) => {
    setWorkingDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  // Tag Filter Logic
  useEffect(() => {
    if (tagSearch.trim().length > 0) {
      const search = tagSearch.toLowerCase();
      const filtered = allTags.filter(tag =>
        tag.name.toLowerCase().includes(search) &&
        !formData.selectedTags.some(selected => selected.id === tag.id)
      );
      setFilteredTags(filtered.slice(0, 5));
    } else {
      setFilteredTags([]);
    }
  }, [tagSearch, allTags, formData.selectedTags]);

  const handleTagSelect = (tag) => {
    setFormData(prev => ({ ...prev, selectedTags: [...prev.selectedTags, tag] }));
    setTagSearch('');
  };

  const handleTagRemove = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      selectedTags: prev.selectedTags.filter(tag => (tag.id ? tag.id !== tagToRemove.id : tag.name !== tagToRemove.name))
    }));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const tagName = tagSearch.trim();
      if (tagName && !formData.selectedTags.some(t => t.name.toLowerCase() === tagName.toLowerCase())) {
        const existingTag = allTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
        if (existingTag) handleTagSelect(existingTag);
        else {
          setFormData(prev => ({ ...prev, selectedTags: [...prev.selectedTags, { id: null, name: tagName }] }));
          setTagSearch('');
        }
      }
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.email.includes('@')) {
      setError('Valid email is required');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Phone is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = { ...formData };

      // Only include password if it's set
      if (!payload.password) {
        delete payload.password;
      }

      // Format working hours
      const hoursFormatted = workingDays.length > 0
        ? `${workingDays.join(', ')} (${timeRange.start}-${timeRange.end})`
        : 'Closed / Appointment Only';
      
      payload.working_hours = hoursFormatted;

      // Format tags for submission
      payload.tags = formData.selectedTags.map(tag => ({ id: tag.id || null, name: tag.name }));

      await httpClient.put(`${ENDPOINTS.ADMIN_API}/vendors/${id}`, payload);

      httpClient.clearCache();

      setSuccess('Vendor updated successfully!');
      setTimeout(() => navigate('/admin/vendors'), 1500);
    } catch (err) {
      setError(err.message || 'Failed to update vendor');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#4f46e5' }} />
      </Box>
    );
  }

  if (error && !formData.name) {
    return (
      <div style={styles.pageContainer}>
        <div style={styles.card}>
          <header style={styles.header}>
            <div style={styles.iconCircle}><PersonIcon sx={{ color: '#4f46e5' }} /></div>
            <h1 style={styles.title}>✏️ Edit Vendor</h1>
            <p style={styles.subtitle}>Update vendor account details</p>
          </header>
          <div style={styles.errorBanner}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      <div style={styles.card}>        {/* Back Button */}
        <button
          type="button"
          onClick={() => navigate('/admin/vendors')}
          style={styles.backButton}
          title="Back to Vendors"
        >
          <ArrowBackIcon sx={{ fontSize: '20px' }} />
        </button>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.iconCircle}><PersonIcon sx={{ color: '#4f46e5' }} /></div>
          <h1 style={styles.title}>✏️ Edit Vendor</h1>
          <p style={styles.subtitle}>Update vendor account details</p>
        </header>

        {error && <div style={styles.errorBanner}>{error}</div>}
        {success && <div style={styles.successBanner}>{success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Section: Personal */}
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <PersonIcon sx={styles.sectionIcon} />
              <h3 style={styles.sectionTitle}>Personal Details</h3>
            </div>

            <div style={styles.formGrid}>
              <div style={styles.inputWrapper}>
                <label style={styles.label}>Full Name *</label>
                <TextField
                  fullWidth
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  placeholder="e.g. John Doe"
                  required
                  sx={styles.muiInput}
                />
              </div>

              <div style={styles.inputWrapper}>
                <label style={styles.label}>Email Address *</label>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  placeholder="john@example.com"
                  required
                  sx={styles.muiInput}
                />
              </div>

              <div style={styles.inputWrapper}>
                <label style={styles.label}>Phone Number *</label>
                <TextField
                  fullWidth
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  placeholder="+91..."
                  required
                  sx={styles.muiInput}
                />
              </div>

              <div style={styles.inputWrapper}>
                <label style={styles.label}>Password (leave blank to keep current)</label>
                <TextField
                  fullWidth
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  placeholder="●●●●●●●●"
                  sx={styles.muiInput}
                />
              </div>
            </div>
          </section>

          {/* Section: Business */}
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <BusinessIcon sx={styles.sectionIcon} />
              <h3 style={styles.sectionTitle}>Business Info</h3>
            </div>

            <div style={{ ...styles.inputWrapper, marginBottom: '20px' }}>
              <label style={styles.label}>Business / Shop Name</label>
              <TextField
                fullWidth
                name="business_name"
                value={formData.business_name}
                onChange={handleInputChange}
                variant="outlined"
                size="small"
                placeholder="e.g. JD Electricals"
                sx={styles.muiInput}
              />
            </div>

            <div style={styles.formGrid}>
              <div style={styles.inputWrapper}>
                <label style={styles.label}>City</label>
                <Select
                  fullWidth
                  name="city_id"
                  value={formData.city_id}
                  onChange={handleInputChange}
                  size="small"
                  displayEmpty
                  sx={styles.muiInput}
                >
                  <MenuItem value="">Select City</MenuItem>
                  {cities.map(city => (
                    <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                  ))}
                </Select>
              </div>

              <div style={styles.inputWrapper}>
                <label style={styles.label}>Area</label>
                <Select
                  fullWidth
                  name="area_id"
                  value={formData.area_id}
                  onChange={handleInputChange}
                  size="small"
                  disabled={!formData.city_id}
                  displayEmpty
                  sx={styles.muiInput}
                >
                  <MenuItem value="">Select Area</MenuItem>
                  {areas.map(area => (
                    <MenuItem key={area.id} value={area.id}>{area.name}</MenuItem>
                  ))}
                </Select>
              </div>
            </div>

            <div style={{ ...styles.inputWrapper, marginBottom: '20px', marginTop: '20px' }}>
              <label style={styles.label}>Full Address</label>
              <TextField
                fullWidth
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                multiline
                rows={2}
                placeholder="Shop number, street, landmark..."
                sx={styles.muiInput}
              />
            </div>

            <div style={styles.formGrid}>
              <div style={styles.inputWrapper}>
                <label style={styles.label}>Latitude</label>
                <TextField
                  fullWidth
                  name="lat"
                  type="number"
                  value={formData.lat}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  inputProps={{ step: '0.000001' }}
                  placeholder="e.g. 28.7041"
                  sx={styles.muiInput}
                />
              </div>

              <div style={styles.inputWrapper}>
                <label style={styles.label}>Longitude</label>
                <TextField
                  fullWidth
                  name="lng"
                  type="number"
                  value={formData.lng}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  inputProps={{ step: '0.000001' }}
                  placeholder="e.g. 77.1025"
                  sx={styles.muiInput}
                />
              </div>
            </div>

            <div style={{ ...styles.formGrid, marginTop: '20px' }}>
              <div style={styles.inputWrapper}>
                <label style={styles.label}>Working Days</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {daysList.map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${workingDays.includes(day) ? '#4f46e5' : '#e2e8f0'}`,
                        background: workingDays.includes(day) ? '#4f46e5' : 'white',
                        color: workingDays.includes(day) ? 'white' : '#1e293b',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        transition: '0.2s',
                      }}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ ...styles.formGrid, marginTop: '20px' }}>
              <div style={styles.inputWrapper}>
                <label style={styles.label}>Opens At</label>
                <input
                  type="time"
                  value={timeRange.start}
                  onChange={e => setTimeRange({ ...timeRange, start: e.target.value })}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div style={styles.inputWrapper}>
                <label style={styles.label}>Closes At</label>
                <input
                  type="time"
                  value={timeRange.end}
                  onChange={e => setTimeRange({ ...timeRange, end: e.target.value })}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
          </section>

          {/* Section: Description */}
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <CheckCircleIcon sx={styles.sectionIcon} />
              <h3 style={styles.sectionTitle}>About Services</h3>
            </div>
            <TextField
              fullWidth
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={3}
              placeholder="Describe the vendor's services..."
              sx={styles.muiInput}
            />
          </section>

          {/* Section: Tags */}
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <LocalOfferIcon sx={styles.sectionIcon} />
              <h3 style={styles.sectionTitle}>Search Tags</h3>
            </div>
            <p style={styles.infoText}>Add keywords like "emergency", "repair", "affordable" to appear in searches.</p>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Type tag and press Enter"
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                sx={styles.muiInput}
              />
              {tagSearch && filteredTags.length > 0 && (
                <div style={styles.suggestionBox}>
                  {filteredTags.map(tag => (
                    <div key={tag.id} onClick={() => handleTagSelect(tag)} style={styles.suggestionItem}>{tag.name}</div>
                  ))}
                </div>
              )}
            </Box>
            <div style={styles.chipContainer}>
              {formData.selectedTags.map(tag => (
                <Chip
                  key={tag.id || tag.name}
                  label={tag.name}
                  onDelete={() => handleTagRemove(tag)}
                  sx={styles.tagChip}
                />
              ))}
            </div>
          </section>

          {/* Section: Verification */}
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <CheckCircleIcon sx={styles.sectionIcon} />
              <h3 style={styles.sectionTitle}>Verification Status</h3>
            </div>
            <div style={styles.inputWrapper}>
              <label style={styles.label}>
                <input
                  type="checkbox"
                  name="is_verified"
                  checked={formData.is_verified}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px', cursor: 'pointer' }}
                />
                <span style={{ cursor: 'pointer' }}>Mark as Verified Vendor</span>
              </label>
            </div>
          </section>

          {/* Buttons */}
          <div style={styles.actions}>
            <button type="button" onClick={() => navigate('/admin/vendors')} style={styles.cancelBtn}>
              Cancel
            </button>
            <button type="submit" disabled={submitting} style={styles.submitBtn}>
              {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Update Vendor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    padding: '24px 16px',
    display: 'flex',
    justifyContent: 'center',
  },
  card: {
    width: '100%',
    maxWidth: '800px',
    backgroundColor: '#fff',
    borderRadius: '24px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    padding: '32px',
    border: '1px solid #e2e8f0',
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    top: '16px',
    left: '16px',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: '0.2s',
    color: '#4f46e5',
    padding: 0,
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  iconCircle: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: '#eef2ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 800,
    color: '#0f172a',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  section: {
    marginBottom: '32px',
    paddingBottom: '24px',
    borderBottom: '1px solid #f1f5f9',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  sectionIcon: {
    color: '#4f46e5',
    fontSize: '20px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#334155',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    marginBottom: '20px',
  },
  inputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#475569',
  },
  muiInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#fff',
      transition: '0.2s',
      '&:hover': { backgroundColor: '#fdfdfd' },
      '&.Mui-focused': {
        backgroundColor: '#fff',
        boxShadow: '0 0 0 4px rgba(79, 70, 229, 0.1)',
      },
    },
  },
  actions: {
    display: 'flex',
    gap: '16px',
    marginTop: '16px',
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    padding: '12px 24px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    color: '#64748b',
    fontWeight: 600,
    cursor: 'pointer',
    transition: '0.2s',
  },
  submitBtn: {
    padding: '12px 32px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#4f46e5',
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
    transition: '0.2s',
  },
  errorBanner: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    color: '#b91c1c',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '24px',
    border: '1px solid #fee2e2',
  },
  successBanner: {
    padding: '12px 16px',
    backgroundColor: '#f0fdf4',
    color: '#15803d',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '24px',
    border: '1px solid #dcfce7',
  },
  suggestionBox: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    zIndex: 10,
    border: '1px solid #e2e8f0',
    marginTop: '4px',
    overflow: 'hidden',
  },
  suggestionItem: {
    padding: '12px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    borderBottom: '1px solid #f1f5f9',
    ':hover': {
      backgroundColor: '#f1f5f9',
    },
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '12px',
  },
  tagChip: {
    backgroundColor: '#eef2ff',
    color: '#4f46e5',
    fontWeight: 600,
    borderRadius: '8px',
  },
  infoText: {
    fontSize: '12px',
    color: '#64748b',
    marginBottom: '12px',
  },
};

export default AdminEditVendor;
